import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Capacitor } from '@capacitor/core';
import {
  Purchases,
  PurchasesPackage,
  PURCHASES_ERROR_CODE,
  INTRO_ELIGIBILITY_STATUS,
} from '@revenuecat/purchases-capacitor';
import { posthog } from 'posthog-js';
import { colors, darkmodeStyles, fontStyles, spacing, safeAreaInset, buildPageSpacing } from '../../styles';
import { waitForPurchases } from '../../utils/purchasesInit';
import Button from '../Button';
import ToggleSwitch from '../ToggleSwitch';
import closeIcon from '../../assets/close.svg';
import * as stores from '../../stores';
import { entitlementIdentifier, ONBOARDING_PAYWALL_SOURCES } from '../../stores';
import { UserSubscriptionService, SubscriptionType } from '../../services/userSubscriptionService';
import { usePromoCode } from '../../hooks/usePromoCode';
import { useNotificationPermissions } from '../../hooks/useNotificationPermissions';
import NotificationBlockedModal from '../NotificationBlockedModal';
import {
  FALLBACK_PRICES,
  PREFERRED_OFFERING_IDS,
  DISCOUNT_PACKAGE_IDENTIFIER,
  findPackageByType,
  findDiscountPackage,
  findPackageInAllOfferings,
  hasValidPackages,
  hasValidOfferingsInResult,
  getOfferTrialDays,
} from '../../utils/subscriptionUtils';
import { calculateSavingsPercentage } from '../../utils/pricing';
import { appsflyerEvents } from '../../utils/appsflyerEvents';
import { reportError, shouldReportPurchaseError } from '../../utils/reporting';
import { completeOnboarding } from '../../utils/onboarding';
import { trackOnboardingCompletion } from '../../utils/analytics';
import { InvestorType } from '../../utils/smartChatPrompts';
import { setInvestorPersona } from '../MorePage/InvestorPersonaSelector';
import { AppBar } from '../OnboardingView/Components/AppBar';
import { OneTimeOfferScreen } from './OneTimeOfferScreen';
import { AnnualPricingCard } from './AnnualPricingCard';
import { OnboardingTrialTimeline } from './OnboardingTrialTimeline';
import { TestimonialsCarousel } from './TestimonialsCarousel';
import { PlanSelectionSheet } from './PlanSelectionSheet';
import { FeatureShowcase } from './FeatureShowcase';
import { TrialHero } from './TrialHero';
import { getTestimonials } from './constants';

/**
 * Get a user-friendly error message for RevenueCat purchase errors.
 * Based on PURCHASES_ERROR_CODE from @revenuecat/purchases-capacitor
 */
const getPurchaseErrorMessage = (errorCode: string | undefined, t: (key: string) => string): string => {
  switch (errorCode) {
    case PURCHASES_ERROR_CODE.PURCHASE_NOT_ALLOWED_ERROR:
      return t('subscription.error.purchaseNotAllowed');
    case PURCHASES_ERROR_CODE.STORE_PROBLEM_ERROR:
      return t('subscription.error.storeProblem');
    case PURCHASES_ERROR_CODE.PRODUCT_NOT_AVAILABLE_FOR_PURCHASE_ERROR:
      return t('subscription.error.productNotAvailable');
    case PURCHASES_ERROR_CODE.PRODUCT_ALREADY_PURCHASED_ERROR:
      return t('subscription.error.productAlreadyPurchased');
    case PURCHASES_ERROR_CODE.NETWORK_ERROR:
      return t('subscription.error.networkError');
    case PURCHASES_ERROR_CODE.PAYMENT_PENDING_ERROR:
      return t('subscription.error.paymentPending');
    case PURCHASES_ERROR_CODE.INELIGIBLE_ERROR:
      return t('subscription.error.ineligible');
    case PURCHASES_ERROR_CODE.OFFLINE_CONNECTION_ERROR:
      return t('subscription.error.offlineConnection');
    case PURCHASES_ERROR_CODE.RECEIPT_ALREADY_IN_USE_ERROR:
    case PURCHASES_ERROR_CODE.RECEIPT_IN_USE_BY_OTHER_SUBSCRIBER_ERROR:
      return t('subscription.error.receiptInUse');
    case PURCHASES_ERROR_CODE.CONFIGURATION_ERROR:
      return t('subscription.error.configurationError');
    default:
      return t('subscription.error.default');
  }
};

const getOnboardingMotivations = (): InvestorType[] => {
  try {
    return JSON.parse(localStorage.getItem('onboardingMotivations') || '[]') as InvestorType[];
  } catch {
    return [];
  }
};

export const PaymentModalByPreference: React.FC = () => {
  const { t } = useTranslation();
  // Payment-related states
  const [isLoading, setIsLoading] = useState(false);
  const offerings = stores.Purchase.useState((s) => s.offerings);
  const trialEligibility = stores.Purchase.useState((s) => s.trialEligibility);

  const [selectedPackage, setSelectedPackage] = useState<PurchasesPackage>();
  // Always default to yearly - it's the better value with free trial
  const [subscriptionType, setSubscriptionType] = useState<'yearly' | 'weekly'>('yearly');
  const { notifyBeforeTrial, handleToggle, isNotificationBlockedModalOpen, setIsNotificationBlockedModalOpen } =
    useNotificationPermissions();

  const [onboardingStep, setOnboardingStep] = useState<1 | 2 | 3>(1);
  const [promoCode, setPromoCode] = useState(false);
  const { handlePromoCode } = usePromoCode();
  const [iosPromoCodeWasOpened, setIosPromoCodeWasOpened] = useState(false);
  const [isFetchingOfferings, setIsFetchingOfferings] = useState(false);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  const [offeringsRetryCount, setOfferingsRetryCount] = useState(0);
  const [isPlanSheetOpen, setIsPlanSheetOpen] = useState(false);
  const MAX_RETRIES = 3;

  // Track if we've already logged a package not found error to prevent telemetry spam
  const hasLoggedPackageNotFoundRef = useRef(false);

  // Original modal states
  const isOpen = stores.General.useState((s) => s.isPaymentModalByPreferenceShown);
  const paymentModalSource = stores.General.useState((s) => s.paymentModalSource);
  const isOnboardingSource = ONBOARDING_PAYWALL_SOURCES.includes(
    paymentModalSource as (typeof ONBOARDING_PAYWALL_SOURCES)[number],
  );
  const isSkipSource = paymentModalSource === 'onboarding_skip' || paymentModalSource === 'onboarding_portfolio_skip';
  const history = useHistory();

  const isNative = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform();

  // Compute discount package early so it can be referenced by handleClose/handleContinueFree
  const earlyTargetOffering = offerings?.all?.['weekly_yearly_pricing'] || offerings?.current;
  const oneTimeDiscountOfferingEarly = offerings?.all?.[DISCOUNT_PACKAGE_IDENTIFIER];
  const discountPackage: PurchasesPackage | null =
    oneTimeDiscountOfferingEarly?.annual ||
    oneTimeDiscountOfferingEarly?.availablePackages?.find((p) => p.packageType === 'ANNUAL') ||
    findDiscountPackage(earlyTargetOffering) ||
    null;

  // Show one-time offer (Screen 3) only if discount package exists on native.
  // On web, always allow it for testing (RevenueCat doesn't work on web).
  const canShowOneTimeOffer = !isNative || !!discountPackage;

  // Track whether we've already fired the step 1 tracking event for this modal open
  const hasTrackedStep1Ref = useRef(false);

  // Prevent background scrolling when modal is open, and reset onboarding step
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (isOnboardingSource) {
        setOnboardingStep(1);
        // Track paywall step 1 view when modal opens during onboarding
        // Only fire once per modal open (not on re-renders or close/reopen)
        if (!hasTrackedStep1Ref.current) {
          hasTrackedStep1Ref.current = true;
          posthog.capture('Onboarding Paywall Step', { step: 1 });
        }
      }
    } else {
      document.body.style.overflow = '';
      // Reset tracking flag when modal closes so it can fire again on next open
      hasTrackedStep1Ref.current = false;
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isOnboardingSource]);

  const handleBackToOnboarding = useCallback(() => {
    stores.hidePaymentModalByPreference();
    history.push('/onboarding/3');
  }, [history]);

  const handleClose = useCallback(async () => {
    console.log('[PaymentModal] Close button clicked');
    const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';
    const hasSeenDiscount = localStorage.getItem('oneTimeDiscountOffered') === 'true';

    // During onboarding Screen 2, show one-time offer (Screen 3) if not seen
    // Note: we set oneTimeDiscountOffered AFTER the user leaves Screen 3 (not on entry) so that
    // shouldShowDiscount remains true while Screen 3 is active, allowing the discount package to be selected
    if (isOnboardingSource && onboardingStep === 2 && !hasSeenDiscount && canShowOneTimeOffer) {
      setOnboardingStep(3);
      posthog.capture('Onboarding Paywall Step', { step: 3, type: 'one_time_offer' });
      return;
    }

    // Mark one-time offer as seen when closing Screen 3
    if (isOnboardingSource && onboardingStep === 3) {
      localStorage.setItem('oneTimeDiscountOffered', 'true');
    }

    // Only complete onboarding if paywall was triggered from the onboarding flow
    if (isOnboardingSource && !hasCompletedOnboarding) {
      // Track onboarding completion for users who dismiss paywall.
      // Skip users completed zero onboarding steps; regular onboarding users completed 6.
      const watchlist = stores.General.getRawState().watchlist || {};
      const selectedSymbols = Object.keys(watchlist).filter((symbol) => watchlist[symbol]);
      const motivations = getOnboardingMotivations();
      const startTime = parseInt(localStorage.getItem('onboardingStartTime') || '0', 10);
      const completionTime = startTime > 0 ? Date.now() - startTime : 0;

      trackOnboardingCompletion({
        totalSteps: isSkipSource ? 0 : 6,
        selectedMotivations: motivations,
        selectedSymbols,
        completionTime,
      });

      // Persist investor persona before cleaning up onboarding data
      if (motivations.length > 0) {
        setInvestorPersona(motivations[0]);
      }

      // Clean up onboarding data
      localStorage.removeItem('onboardingMotivations');
      localStorage.removeItem('onboardingStartTime');

      completeOnboarding();

      // Non-subscribers skip result page and go directly to ideas
      history.push('/chat');
    }

    stores.hidePaymentModalByPreference();
  }, [history, isOnboardingSource, onboardingStep, canShowOneTimeOffer, isSkipSource]);

  // Check if we're in onboarding mode - must be declared before callbacks that use it
  const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';
  const isOnboarding = !hasCompletedOnboarding;

  const handleContinueFree = useCallback(() => {
    posthog.capture('paywall_continue_free_clicked', {
      source: isOnboarding ? 'onboarding' : 'in_app',
      subscription_type: subscriptionType,
    });

    // During onboarding Screen 2, show one-time offer (Screen 3) if not seen and discount package available
    if (isOnboardingSource && onboardingStep === 2 && canShowOneTimeOffer) {
      const hasSeenDiscount = localStorage.getItem('oneTimeDiscountOffered') === 'true';
      if (!hasSeenDiscount) {
        setOnboardingStep(3);
        posthog.capture('Onboarding Paywall Step', { step: 3, type: 'one_time_offer', trigger: 'explore_free' });
        return;
      }
    }

    const hasCompletedOnboarding = localStorage.getItem('hasCompletedOnboarding') === 'true';

    if (!hasCompletedOnboarding) {
      // Skip users completed zero onboarding steps; regular onboarding users completed 6.
      const watchlist = stores.General.getRawState().watchlist || {};
      const selectedSymbols = Object.keys(watchlist).filter((symbol) => watchlist[symbol]);
      const motivations = getOnboardingMotivations();
      const startTime = parseInt(localStorage.getItem('onboardingStartTime') || '0', 10);
      const completionTime = startTime > 0 ? Date.now() - startTime : 0;

      trackOnboardingCompletion({
        totalSteps: isSkipSource ? 0 : 6,
        selectedMotivations: motivations,
        selectedSymbols,
        completionTime,
      });

      if (motivations.length > 0) {
        setInvestorPersona(motivations[0]);
      }

      localStorage.removeItem('onboardingMotivations');
      localStorage.removeItem('onboardingStartTime');

      completeOnboarding();
      history.push('/chat');
    }

    stores.hidePaymentModalByPreference();
  }, [history, isOnboarding, isOnboardingSource, onboardingStep, subscriptionType, canShowOneTimeOffer, isSkipSource]);

  // Function to fetch offerings with retry
  const fetchOfferings = useCallback(async () => {
    // Guard against concurrent fetches and eligibility checks
    if (!isNative || isFetchingOfferings || isCheckingEligibility) return;

    setIsFetchingOfferings(true);
    try {
      // Ensure configure() has finished (incl. Android BillingClient connection)
      // before calling any SDK method — avoids PURCHASE_NOT_ALLOWED_ERROR race condition
      await waitForPurchases();
      const result = await Purchases.getOfferings();
      const { customerInfo } = await Purchases.getCustomerInfo();

      // Validate that we have valid packages
      const hasValidOfferings = hasValidOfferingsInResult(result);

      if (!hasValidOfferings) {
        // Safely serialize offerings structure for telemetry
        let offeringsStructure = 'serialization_error';
        try {
          offeringsStructure = JSON.stringify({
            current: result?.current ? { identifier: result.current.identifier } : null,
            all: result?.all ? Object.keys(result.all) : [],
          });
        } catch {
          // Keep default value
        }

        const errorContext = {
          hasCurrent: !!result?.current,
          currentHasPackages: result?.current ? hasValidPackages(result.current) : false,
          allOfferingsCount: result?.all ? Object.keys(result.all).length : 0,
          platform,
          userLocale: navigator.language || 'en-US',
          offeringsStructure,
        };

        console.warn('[PaymentModal] Offerings fetched but no valid packages found', errorContext);

        // Log to telemetry and Sentry
        try {
          posthog.capture('Subscription Packages Unavailable', errorContext);
          reportError(new Error('Offerings fetched but no valid packages found'), errorContext);
        } catch (telemetryError) {
          console.error('Failed to log telemetry:', telemetryError);
        }
      }

      // Update offerings in store
      stores.Purchase.update((s) => {
        s.offerings = result;
        s.purchaserInfo = customerInfo;
      });

      // Only reset retry count when we have valid packages (prevents infinite loop)
      if (hasValidOfferings) {
        setOfferingsRetryCount(0);
      } else {
        // Increment retry count even on success with no valid packages to prevent infinite loop
        setOfferingsRetryCount((prev) => prev + 1);
      }

      // Check trial eligibility for annual package (iOS only - Android always returns UNKNOWN)
      // This is used to show accurate "Free for 3 days" messaging only to eligible users
      // IMPORTANT: We must wait for this check before showing trial messaging to prevent
      // users from seeing "Free for 3 days" when they're actually ineligible
      //
      // Use unstable_batchedUpdates to ensure these state updates are atomic in React 17.
      // This prevents a race condition where stale eligibility data from a previous session
      // could briefly show "Start free trial" before the new eligibility check completes.
      unstable_batchedUpdates(() => {
        setIsFetchingOfferings(false);
        setIsCheckingEligibility(true);
        // Clear stale eligibility to prevent showing trial based on outdated data
        stores.Purchase.update((s) => {
          s.trialEligibility = undefined;
        });
      });
      try {
        const targetOffer = result?.all?.['weekly_yearly_pricing'] || result?.current;
        const annualPkg = findPackageByType(targetOffer, 'ANNUAL');
        if (annualPkg?.product?.identifier) {
          const eligibility = await Purchases.checkTrialOrIntroductoryPriceEligibility({
            productIdentifiers: [annualPkg.product.identifier],
          });
          stores.Purchase.update((s) => {
            s.trialEligibility = eligibility;
          });

          // Track trial eligibility status for analytics
          // This helps us understand how many Android users see conservative messaging
          const eligibilityStatus = eligibility[annualPkg.product.identifier]?.status;
          if (eligibilityStatus === INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_UNKNOWN) {
            posthog.capture('Trial Eligibility Unknown', {
              platform,
              productId: annualPkg.product.identifier,
            });
          }
        }
      } catch (eligibilityError) {
        // Non-critical - but we should NOT default to showing trial messaging
        // as this could lead to users being charged when they expect a free trial
        console.warn('Could not check trial eligibility:', eligibilityError);
      } finally {
        setIsCheckingEligibility(false);
      }
    } catch (error) {
      console.error('Error fetching offerings:', error);

      // Clear stale eligibility when offerings fail to load
      // With no eligibility data, we conservatively hide trial messaging
      stores.Purchase.update((s) => {
        s.trialEligibility = undefined;
      });

      // Log error to telemetry
      try {
        const userLocale = navigator.language || 'en-US';
        posthog.capture('Fetch Offerings Error', {
          platform,
          error: error instanceof Error ? error.message : String(error),
          retryCount: offeringsRetryCount,
          userLocale,
        });
        reportError(error instanceof Error ? error : new Error(String(error)), {
          platform,
          retryCount: offeringsRetryCount,
        });
      } catch (telemetryError) {
        console.error('Failed to log telemetry:', telemetryError);
      }

      // Retry with exponential backoff
      if (offeringsRetryCount < MAX_RETRIES) {
        const backoffTime = Math.pow(2, offeringsRetryCount) * 1000;
        // Increment retry count immediately to prevent race conditions
        setOfferingsRetryCount((prev) => prev + 1);

        setTimeout(() => {
          setIsFetchingOfferings(false);
        }, backoffTime);
      } else {
        // After max retries, log error but don't show toast (may be emulator or dev environment)
        console.warn(
          'Unable to load subscription options after retries - this is expected in emulator/dev environments',
        );
        setIsFetchingOfferings(false);
      }
    }
  }, [isNative, isFetchingOfferings, isCheckingEligibility, offeringsRetryCount, platform]);

  // Go to results page after successful payment
  const handleStartSubscription = useCallback(() => {
    // Ensure hasCompletedOnboarding is not set until after payment
    localStorage.removeItem('hasCompletedOnboarding');
    // Persist total onboarding steps for analytics on Result page.
    localStorage.setItem('onboardingCompletionTotalSteps', isSkipSource ? '0' : '6');
    // Mark user as subscriber for result page
    localStorage.setItem('hasSubscribed', 'true');
    stores.hidePaymentModalByPreference();
    history.push('/result');
  }, [history, isSkipSource]);

  const startSubscriptionWithPromoCode = useCallback(() => {
    // Ensure hasCompletedOnboarding is not set until after payment
    localStorage.removeItem('hasCompletedOnboarding');
    // Persist total onboarding steps for analytics on Result page.
    localStorage.setItem('onboardingCompletionTotalSteps', isSkipSource ? '0' : '6');
    // Mark user as subscriber for result page
    localStorage.setItem('hasSubscribed', 'true');
    stores.hidePaymentModalByPreference();
    history.push('/result');
  }, [history, isSkipSource]);

  const handleRestore = useCallback(async () => {
    setIsLoading(true);
    try {
      if (Capacitor.isNativePlatform()) {
        const { customerInfo } = await Purchases.restorePurchases();

        if (customerInfo.entitlements.active[entitlementIdentifier]) {
          stores.Purchase.update((s) => {
            s.purchaserInfo = customerInfo;
          });
          toast.success(t('subscription.purchaseRestored'));
          handleStartSubscription();
        } else {
          toast.info(t('subscription.noActiveSubscription'));
        }
      } else {
        // For web testing
        setTimeout(() => {
          toast.info(t('subscription.restoreMobileOnly'));
        }, 500);
      }
    } catch (error) {
      console.error('Restore purchases error:', error);
      toast.error(t('subscription.restoreFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [handleStartSubscription, t]);

  const handlePlanSelect = (type: 'yearly' | 'weekly') => {
    setSubscriptionType(type);
  };

  // Debug: Check for ?forceDiscount=true URL parameter to show discount UI on web
  const forceDiscount = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('forceDiscount') === 'true';
  }, []);

  // Check if we should show the 50% off discount (only during onboarding AND one-time discount not offered yet)
  const hasSeenDiscount = localStorage.getItem('oneTimeDiscountOffered') === 'true';
  const shouldShowDiscount = forceDiscount || (isOnboardingSource && !hasSeenDiscount);

  // Calculate pricing details
  // Find the relevant offering - first try weekly_yearly_pricing, then new_pricing, then fall back to current
  const weeklyYearlyOffering = offerings?.all?.['weekly_yearly_pricing'];
  const targetOffering = weeklyYearlyOffering || offerings?.current;

  // Get annual, monthly, and weekly packages
  const annualPackage = findPackageByType(targetOffering, 'ANNUAL');
  const weeklyPackage = findPackageByType(targetOffering, 'WEEKLY');

  // Check if user is eligible for free trial
  // IMPORTANT: We must NOT show trial messaging until eligibility is confirmed to prevent
  // users from being charged when they expect a free trial
  //
  // Android-specific behavior: RevenueCat's checkTrialOrIntroPriceEligibility always returns
  // UNKNOWN on Android because Google Play doesn't provide this information via their API.
  // To avoid misleading users, we show conservative messaging when eligibility is UNKNOWN.
  // Debug: Check for ?forceTrialUI=true URL parameter to show trial UI on web
  const forceTrialUI = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('forceTrialUI') === 'true';
  }, []);

  const isTrialEligible = useMemo(() => {
    // Debug override: force trial UI on web for testing
    if (forceTrialUI) return true;

    // While eligibility is being checked, don't show trial messaging
    if (isCheckingEligibility) return false;

    const productId = annualPackage?.product?.identifier;
    if (!productId) return false; // No product ID means we can't verify eligibility

    const eligibility = trialEligibility?.[productId];
    // If eligibility hasn't loaded yet, don't show trial messaging
    // This prevents users from seeing "Free for 3 days" when they might be ineligible
    if (!eligibility) return false;

    // Only show "Free for 3 days" trial messaging when explicitly ELIGIBLE (iOS can confirm this)
    // When UNKNOWN (Android) or INELIGIBLE, show conservative "Subscribe" messaging instead
    // This prevents users from expecting a free trial when they may not be eligible
    return eligibility.status === INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_ELIGIBLE;
  }, [annualPackage, trialEligibility, isCheckingEligibility, forceTrialUI]);

  // Check if eligibility is unknown (Android always returns this)
  // Used to show a helpful note that trials may be available
  const isTrialEligibilityUnknown = useMemo(() => {
    // Debug override: if forcing trial UI, don't show unknown state
    if (forceTrialUI) return false;

    if (isCheckingEligibility) return false;
    const productId = annualPackage?.product?.identifier;
    if (!productId) return false;
    const eligibility = trialEligibility?.[productId];
    if (!eligibility) return false;
    return eligibility.status === INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_UNKNOWN;
  }, [annualPackage, trialEligibility, isCheckingEligibility, forceTrialUI]);

  // Also search all offerings for weekly package as a fallback
  const weeklyPackageFromAllOfferings = weeklyPackage || findPackageInAllOfferings(offerings ?? null, 'WEEKLY');
  const hasWeeklyPackage = !!weeklyPackageFromAllOfferings;

  // Switch to yearly if weekly is selected but no weekly package is available
  useEffect(() => {
    if (offerings && subscriptionType === 'weekly' && !hasWeeklyPackage) {
      setSubscriptionType('yearly');
    }
  }, [offerings, subscriptionType, hasWeeklyPackage]);

  // Get pricing details - use weeklyPackageFromAllOfferings to get correct price when weekly is in a different offering
  const annualPrice = annualPackage?.product?.price || FALLBACK_PRICES.ANNUAL;
  const weeklyPrice = weeklyPackageFromAllOfferings?.product?.price || FALLBACK_PRICES.WEEKLY;

  // Determine the currency symbol from the package price string
  const currencySymbol =
    annualPackage?.product?.priceString?.[0] || weeklyPackageFromAllOfferings?.product?.priceString?.[0] || '$';

  // Calculate discount price (annual) — discountPackage is computed at the top of the component
  const discountAnnualPrice = discountPackage?.product?.price || FALLBACK_PRICES.DISCOUNT_ANNUAL; // $52/year discount

  // Derive free trial days from the package intro price (RevenueCat).
  // Fallback to 7 (the standard free-trial offer length) when intro price data is unavailable.
  const offerTrialDays = getOfferTrialDays(discountPackage || annualPackage, 7);

  // Wrap handleToggle to show toast when user enables notifications
  const handleNotifyToggle = useCallback(() => {
    const wasOff = !notifyBeforeTrial;
    handleToggle();
    if (wasOff) {
      toast.info(t('subscription.notifyReminder', { day: offerTrialDays - 1 }), { autoClose: 3000 });
    }
  }, [notifyBeforeTrial, handleToggle, offerTrialDays, t]);

  // Use actual prices for display (we'll show both original and discount prices with strikethrough)
  const displayYearlyPrice = annualPrice;
  const displayWeeklyPrice = weeklyPrice;

  // render start trial - THE MOST COMPLEX FUNCTION
  const handleStartTrial = async (
    isDiscounted = false,
    discountPercentage = 0,
    overrideSubscriptionType?: 'yearly' | 'weekly',
    overridePackage?: PurchasesPackage,
    fromWeeklyOffer = false,
  ) => {
    // Guard against race conditions when fetching offerings
    if (isFetchingOfferings) {
      return;
    }

    const effectiveSubscriptionType = overrideSubscriptionType || subscriptionType;
    let effectivePackage: PurchasesPackage | undefined = overridePackage || selectedPackage;
    const desiredType = effectiveSubscriptionType === 'yearly' ? 'ANNUAL' : 'WEEKLY';

    // Verify package type matches subscription type to prevent race condition
    // (user may click Subscribe before useEffect updates selectedPackage)
    const useDiscountFallback = shouldShowDiscount && desiredType === 'ANNUAL' && discountPackage;
    if (effectivePackage && offerings && effectivePackage.packageType !== desiredType) {
      if (useDiscountFallback) {
        effectivePackage = discountPackage!;
      } else {
        const correctPackage = findPackageInAllOfferings(offerings, desiredType);
        effectivePackage = correctPackage || undefined;
      }
    }

    // If no package found, try to find one as a last resort
    if (!effectivePackage && offerings) {
      if (useDiscountFallback) {
        effectivePackage = discountPackage!;
      } else {
        const foundPackage = findPackageInAllOfferings(offerings, desiredType);
        effectivePackage = foundPackage || undefined;
      }
    }

    if (!effectivePackage) {
      // Log detailed error information
      const userLocale = navigator.language || 'en-US';
      const errorContext = {
        platform,
        subscriptionType: effectiveSubscriptionType,
        desiredType,
        hasOfferings: !!offerings,
        hasCurrent: !!offerings?.current,
        currentHasPackages: offerings?.current ? hasValidPackages(offerings.current) : false,
        allOfferingsCount: offerings?.all ? Object.keys(offerings.all).length : 0,
        userLocale,
        fromWeeklyOffer,
      };

      console.error('[PaymentModal] No subscription package available', errorContext);

      // Log to telemetry
      try {
        posthog.capture('No Subscription Package Available Error', errorContext);
        reportError(new Error('No subscription package available when attempting purchase'), errorContext);
      } catch (telemetryError) {
        console.error('Failed to log telemetry:', telemetryError);
      }

      // Show user-friendly error message
      // This often happens when the app is installed from a third-party source (not App Store/Play Store)
      toast.error(t('subscription.subscriptionUnavailable'), { autoClose: 7000 });

      // If offerings are not available or invalid, try to fetch them
      if ((!offerings || !hasValidOfferingsInResult(offerings)) && isNative && !isFetchingOfferings) {
        toast.info(t('subscription.refreshingPackages'), { autoClose: 3000 });
        fetchOfferings();
      }
      return;
    }

    setIsLoading(true);

    try {
      if (Capacitor.isNativePlatform()) {
        // Create offering options with discount if applicable
        const purchaseOptions = { aPackage: effectivePackage };

        // Apply discount if needed using already derived discountPackage
        if (isDiscounted && discountPercentage > 0) {
          if (discountPackage) {
            purchaseOptions.aPackage = discountPackage;
          }
        }

        const { customerInfo } = await Purchases.purchasePackage(purchaseOptions);

        if (customerInfo.entitlements.active[entitlementIdentifier]) {
          stores.Purchase.update((s) => {
            s.purchaserInfo = customerInfo;
          });

          // Track subscription purchase in AppsFlyer and PostHog
          try {
            const packagePrice = effectivePackage?.product?.price ?? 0;
            const packageCurrency = effectivePackage?.product?.currencyCode ?? 'USD';
            const planName = effectiveSubscriptionType === 'yearly' ? 'Annual Plan' : 'Weekly Plan';
            const entitlement = customerInfo.entitlements.active[entitlementIdentifier];

            // Check if this is a trial purchase
            if (entitlement?.periodType === 'TRIAL') {
              await appsflyerEvents.subscription.trialStarted(planName, offerTrialDays, packagePrice, packageCurrency);
              console.log('[APPSFLYER] Trial started tracked successfully');
            }

            await appsflyerEvents.subscription.purchased(planName, packagePrice, packageCurrency);
            console.log('[APPSFLYER] Subscription purchase tracked successfully');

            // Track in PostHog
            posthog.capture('User Subscribed', {
              plan: planName,
              price: packagePrice,
              currency: packageCurrency,
              periodType: entitlement?.periodType,
              packageIdentifier: effectivePackage?.identifier,
            });
          } catch (error) {
            // Business-critical: Report revenue tracking failures to Sentry
            const trackingError = new Error(`Failed to track subscription purchase: ${error}`);
            reportError(trackingError);
          }

          // Register the subscription in our backend
          if (notifyBeforeTrial) {
            const subscriptionService = UserSubscriptionService.getInstance();
            const subscriptionResult = await subscriptionService.registerSubscription(
              SubscriptionType.free,
              offerTrialDays,
            );

            if (!subscriptionResult.success) {
              toast.error(t('subscription.errorRegistering', { message: subscriptionResult.message }));
            }
          }

          setOnboardingStep(1);
          toast.success(t('subscription.trialStarted'));
          handleStartSubscription();
        } else {
          throw new Error('Entitlement not active');
        }
      } else {
        // For web testing - simulate subscription
        // Web testing - applying discount

        setTimeout(() => {
          setOnboardingStep(1);
          toast.success(t('subscription.trialStarted'));
          handleStartSubscription();
        }, 1000);
      }
    } catch (error: any) {
      // Get price info for tracking
      const packagePrice = effectivePackage?.product?.price ?? 0;
      const packageCurrency = effectivePackage?.product?.currencyCode ?? 'USD';
      const planName = effectiveSubscriptionType === 'yearly' ? 'Annual Plan' : 'Weekly Plan';

      if (error && error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        // User cancelled the purchase - track this for funnel analysis
        try {
          posthog.capture('Purchase Cancelled', {
            plan: planName,
            plan_type: effectiveSubscriptionType,
            price: packagePrice,
            currency: packageCurrency,
            package_identifier: effectivePackage?.identifier,
            from_weekly_offer: fromWeeklyOffer,
            stage: 'app_store_dialog',
          });
        } catch (trackingError) {
          console.error('Failed to track purchase cancellation:', trackingError);
        }

        // During onboarding Screen 2, show one-time offer (Screen 3) if purchase is cancelled and discount package available
        if (isOnboardingSource && onboardingStep === 2 && canShowOneTimeOffer) {
          const hasSeenDiscount = localStorage.getItem('oneTimeDiscountOffered') === 'true';
          if (!hasSeenDiscount) {
            setTimeout(() => {
              setOnboardingStep(3);
              posthog.capture('Onboarding Paywall Step', {
                step: 3,
                type: 'one_time_offer',
                trigger: 'purchase_cancelled',
              });
            }, 500);
          }
        }
      } else {
        // Log purchase error to Sentry
        const errorContext = {
          platform,
          subscriptionType: effectiveSubscriptionType,
          packageIdentifier: effectivePackage?.identifier,
          packageType: effectivePackage?.packageType,
          errorCode: error?.code,
          errorMessage: error?.message || String(error),
          userLocale: navigator.language || 'en-US',
        };

        console.error('[PaymentModal] Purchase error:', errorContext);
        if (shouldReportPurchaseError(error?.code)) {
          reportError(error instanceof Error ? error : new Error(`Purchase failed: ${String(error)}`), errorContext);
        }

        // Track purchase error in PostHog for funnel analysis
        try {
          posthog.capture('Purchase Error', {
            plan: planName,
            plan_type: effectiveSubscriptionType,
            price: packagePrice,
            currency: packageCurrency,
            package_identifier: effectivePackage?.identifier,
            error_code: error?.code,
            error_message: error?.message || String(error),
            stage: 'app_store_dialog',
          });
        } catch (trackingError) {
          console.error('Failed to track purchase error:', trackingError);
        }

        // Show specific error message based on error code
        toast.error(getPurchaseErrorMessage(error?.code, t));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper for button onClick that doesn't need parameters
  const handleStartTrialClick = () => {
    // Guard: don't proceed if no package is available (defense-in-depth for disabled button bypass)
    if (!selectedPackage) {
      if (isNative && !isFetchingOfferings) {
        toast.info(t('subscription.refreshingPackages'), { autoClose: 3000 });
        fetchOfferings();
      }
      return;
    }

    // Track free trial click event
    try {
      posthog.capture('Free Trial Clicked', {
        plan_type: subscriptionType,
        price: subscriptionType === 'yearly' ? displayYearlyPrice : displayWeeklyPrice,
        currency_symbol: currencySymbol,
        trial_days: offerTrialDays,
      });
    } catch (error) {
      console.error('Failed to track free trial click:', error);
    }

    handleStartTrial();
  };

  // Trigger fetch when retry count changes or on first load
  // Also trigger if we have offerings but no valid packages
  useEffect(() => {
    if (!isNative || isFetchingOfferings) return;
    // Prevent infinite loop when max retries reached
    if (offeringsRetryCount >= MAX_RETRIES) return;

    if (!offerings || !hasValidOfferingsInResult(offerings)) {
      fetchOfferings();
    }
  }, [offerings, isNative, isFetchingOfferings, fetchOfferings, offeringsRetryCount]);

  // Initialize selectedPackage when inputs change (collapsed selection)
  // Uses multiple fallback strategies to find packages
  useEffect(() => {
    if (!offerings) return;

    const desiredType = subscriptionType === 'yearly' ? 'ANNUAL' : 'WEEKLY';

    // Strategy 1: Try discount package when actively showing discount price to user
    const useDiscountPackage = shouldShowDiscount && subscriptionType === 'yearly' && isNative && discountPackage;
    let selectedPkg: PurchasesPackage | null | undefined = useDiscountPackage ? discountPackage : null;

    // Strategy 2: Try target offering (weekly_yearly_pricing or current)
    if (!selectedPkg && targetOffering) {
      selectedPkg =
        (targetOffering.availablePackages?.find((pkg) => pkg.packageType === desiredType) as
          | PurchasesPackage
          | undefined) ||
        (subscriptionType === 'yearly'
          ? annualPackage || targetOffering.annual
          : weeklyPackage || targetOffering.weekly) ||
        null;
    }

    // Strategy 3: Search in all offerings if not found in target offering
    if (!selectedPkg) {
      selectedPkg = findPackageInAllOfferings(offerings, desiredType, PREFERRED_OFFERING_IDS);
    }

    // Strategy 4: Try any offering if still not found
    if (!selectedPkg) {
      selectedPkg = findPackageInAllOfferings(offerings, desiredType);
    }

    if (!selectedPkg) {
      // For WEEKLY packages: if no weekly package exists in any offering, this is expected
      // (not all RevenueCat configurations have weekly packages) - don't report as error
      // The useEffect above will switch to yearly, so this is just a transient state
      if (desiredType === 'WEEKLY') {
        console.info(`[PaymentModal] No weekly package available - this is expected for some configurations`);
        setSelectedPackage(undefined);
        return;
      }

      // For ANNUAL packages: this is unexpected and should be reported
      // Only log telemetry once per session to prevent spam when switching subscription types
      if (!hasLoggedPackageNotFoundRef.current) {
        hasLoggedPackageNotFoundRef.current = true;

        // Log detailed information when package is not found
        const userLocale = navigator.language || 'en-US';

        // Safely serialize offerings structure for telemetry
        let offeringsStructure = 'serialization_error';
        try {
          offeringsStructure = JSON.stringify({
            current: offerings?.current
              ? {
                  identifier: offerings.current.identifier,
                  hasPackages: hasValidPackages(offerings.current),
                }
              : null,
            all: offerings?.all
              ? Object.keys(offerings.all).map((id) => ({
                  id,
                  hasPackages: hasValidPackages(offerings.all[id]),
                }))
              : [],
          });
        } catch {
          // Keep default value
        }

        const errorContext = {
          platform,
          subscriptionType,
          desiredType,
          hasOfferings: !!offerings,
          hasCurrent: !!offerings?.current,
          currentHasPackages: offerings?.current ? hasValidPackages(offerings.current) : false,
          allOfferingsCount: offerings?.all ? Object.keys(offerings.all).length : 0,
          targetOfferingId: targetOffering?.identifier,
          userLocale,
          offeringsStructure,
        };

        console.warn(`[PaymentModal] ${desiredType} package not found`, errorContext);

        // Log to telemetry and Sentry
        try {
          posthog.capture('Subscription Package Not Found', errorContext);
          reportError(
            new Error(`${desiredType} subscription package not found after all fallback strategies`),
            errorContext,
          );
        } catch (telemetryError) {
          console.error('Failed to log telemetry:', telemetryError);
        }
      }

      setSelectedPackage(undefined);
      return;
    }

    // Reset the flag when we successfully find a package
    hasLoggedPackageNotFoundRef.current = false;

    // At this point selectedPkg is guaranteed to be non-null (early return above)
    const pkg = selectedPkg as PurchasesPackage;
    setSelectedPackage((prev) => (prev?.identifier !== pkg.identifier ? pkg : prev));
  }, [
    offerings,
    targetOffering,
    discountPackage,
    annualPackage,
    weeklyPackage,
    subscriptionType,
    isNative,
    shouldShowDiscount,
    platform,
  ]);

  // Launch native promo code redemption interface when promoCode is true
  useEffect(() => {
    const getPromoCode = localStorage.getItem('promoCode');
    if (getPromoCode === 'true' && Capacitor.isNativePlatform()) {
      setPromoCode(true);
      localStorage.removeItem('promoCode');
    }
  }, []);

  // Handle promo code application when the state changes to true
  useEffect(() => {
    if (promoCode && Capacitor.isNativePlatform()) {
      const applyPromoCode = async () => {
        try {
          // First open the appropriate store based on platform
          if (platform === 'android') {
            await handlePromoCode(); // This opens the Google Play redeem page
            // Check if the promo code was applied successfully
            const { customerInfo } = await Purchases.getCustomerInfo();
            if (customerInfo.entitlements.active[entitlementIdentifier]) {
              stores.Purchase.update((s) => {
                s.purchaserInfo = customerInfo;
              });
              toast.success(t('subscription.promoCodeApplied'));
              startSubscriptionWithPromoCode();
            }
          } else if (platform === 'ios' && !iosPromoCodeWasOpened) {
            // For iOS use the native RevenueKit method
            setIosPromoCodeWasOpened(true);
            await Purchases.presentCodeRedemptionSheet();
            // Check if the promo code was applied successfully
            const { customerInfo } = await Purchases.getCustomerInfo();
            if (customerInfo.entitlements.active[entitlementIdentifier]) {
              stores.Purchase.update((s) => {
                s.purchaserInfo = customerInfo;
              });
              toast.success(t('subscription.promoCodeApplied'));
              startSubscriptionWithPromoCode();
            }
          }
        } catch (error) {
          console.error(`Error processing promo code for ${platform}:`, error);
          setPromoCode(false);
        }
      };

      applyPromoCode();
    }
  }, [promoCode, handlePromoCode, platform, startSubscriptionWithPromoCode, iosPromoCodeWasOpened, t]);

  if (!isOpen) return null;

  return (
    <div
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        height: 100vh;
        z-index: 1000;
        background-color: ${colors.sugar};
        ${darkmodeStyles.regular}
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          height: 100%;
          position: relative;
          overflow: hidden;
        `}
      >
        {/* Header */}
        {isOnboardingSource && onboardingStep === 1 ? (
          paymentModalSource === 'onboarding_completion' ? (
            <div
              css={css`
                flex-shrink: 0;
                padding-top: ${safeAreaInset.top};
              `}
            >
              <AppBar
                onBack={handleBackToOnboarding}
                showBackButton={true}
                showProgress={true}
                progressIndex={4}
                totalSteps={4}
              />
            </div>
          ) : (
            <div
              css={css`
                flex-shrink: 0;
                padding-top: ${safeAreaInset.top};
              `}
            />
          )
        ) : isOnboardingSource && onboardingStep === 2 ? (
          /* Screen 2: close button top-right only */
          <header
            css={css`
              display: flex;
              justify-content: flex-end;
              padding: 16px 16px 0 16px;
              padding-top: ${safeAreaInset.top};
              flex-shrink: 0;
              background-color: ${colors.sugar};
              ${darkmodeStyles.regular}
              position: relative;
              z-index: 100;
            `}
          >
            <button
              onClick={handleClose}
              type="button"
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                padding: 0;
                border: none;
                background: none;
                cursor: pointer;
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
              `}
            >
              <img
                src={closeIcon}
                alt={t('common.close')}
                css={css`
                  width: 18px;
                  height: 18px;
                  pointer-events: none;
                `}
              />
            </button>
          </header>
        ) : (
          <header
            css={css`
              display: flex;
              justify-content: flex-end;
              padding: 16px 16px 0 16px;
              padding-top: ${safeAreaInset.top};
              flex-shrink: 0;
              background-color: ${colors.sugar};
              ${darkmodeStyles.regular}
              position: relative;
              z-index: 100;
            `}
          >
            <button
              onClick={handleClose}
              type="button"
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 32px;
                height: 32px;
                padding: 0;
                border: none;
                background: none;
                cursor: pointer;
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
              `}
            >
              <img
                src={closeIcon}
                alt={t('common.close')}
                css={css`
                  width: 18px;
                  height: 18px;
                  pointer-events: none;
                `}
              />
            </button>
          </header>
        )}

        {/* Body - Scrollable content */}
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
            padding: ${spacing.milli} ${spacing.regular};
            padding-bottom: calc(
              ${isOnboardingSource && onboardingStep === 1 ? '100px' : '300px'} + ${safeAreaInset.bottom} + 20px
            );
          `}
        >
          <div
            css={css`
              max-width: 600px;
              margin: 0 auto;
            `}
          >
            {isOnboardingSource && onboardingStep === 2 ? (
              <>
                {/* Screen 2: Trial timeline + pricing */}
                <TrialHero
                  isTrialEligible={isTrialEligible || isTrialEligibilityUnknown}
                  showDiscount={shouldShowDiscount}
                />

                <OnboardingTrialTimeline trialDays={offerTrialDays} />

                {/* Notification toggle — moved from footer */}
                {subscriptionType === 'yearly' && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      padding: ${spacing.deci};
                      background-color: ${colors.fog};
                      border: 0.5px solid ${colors.cloud};
                      border-radius: 8px;
                      margin-bottom: ${spacing.deci};

                      html[data-theme='dark'] & {
                        background-color: ${colors.tungsten};
                      }
                    `}
                  >
                    <span
                      css={css`
                        ${fontStyles.lion}
                        color: ${colors.liquorice};

                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {t('subscription.notifyBeforeTrialEnds')}
                    </span>
                    <ToggleSwitch checked={notifyBeforeTrial} onChange={handleNotifyToggle} />
                  </div>
                )}

                <AnnualPricingCard
                  annualPrice={shouldShowDiscount ? discountAnnualPrice : displayYearlyPrice}
                  currencySymbol={currencySymbol}
                  weeklyEquivalent={
                    Math.round(((shouldShowDiscount ? discountAnnualPrice : displayYearlyPrice) / 52) * 100) / 100
                  }
                  originalPrice={shouldShowDiscount ? displayYearlyPrice : undefined}
                />
              </>
            ) : (
              <>
                {/* Screen 1 (onboarding) or non-onboarding: trust content */}
                <TrialHero
                  isTrialEligible={isTrialEligible || isTrialEligibilityUnknown}
                  showDiscount={shouldShowDiscount}
                />

                <FeatureShowcase
                  css={css`
                    margin: 0 0 ${spacing.deka} 0;
                  `}
                />

                <h2
                  css={css`
                    ${fontStyles.shark};
                    color: ${colors.liquorice};
                    text-align: center;
                    margin: 0 0 ${spacing.centi} 0;

                    html[data-theme='dark'] & {
                      color: ${colors.sugar};
                    }
                  `}
                >
                  {t('subscription.testimonials')}
                </h2>

                <TestimonialsCarousel testimonials={getTestimonials(t)} />

                <div
                  css={css`
                    height: 24px;
                  `}
                />
              </>
            )}

            {/* Trust badge — shown on all paywall views */}
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: ${spacing.centi};
                padding: ${spacing.deci} 0;
                ${fontStyles.fatCat};
                color: ${colors.leaf};
              `}
            >
              <span>✓</span> {t('subscription.noCommitmentCancelAnytime')}
            </div>
          </div>
        </div>

        {/* Footer - Floating */}
        <div
          css={css`
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: ${colors.sugar};
            border-top: 1px solid ${colors.cloud};
            padding: ${spacing.deci} ${spacing.regular};
            padding-bottom: ${buildPageSpacing(spacing.deci, 'BOTTOM')};
            ${darkmodeStyles.regular}
            z-index: 10;

            html[data-theme='dark'] & {
              border-top-color: ${colors.tungsten};
            }
          `}
        >
          <div
            css={css`
              max-width: 600px;
              margin: 0 auto;
              display: flex;
              flex-direction: column;
              gap: ${spacing.deci};
            `}
          >
            {isOnboardingSource && onboardingStep === 1 ? (
              /* Screen 1: single Continue button */
              <Button
                variant="primary"
                block
                onClick={() => {
                  setOnboardingStep(2);
                  posthog.capture('Onboarding Paywall Step', { step: 2 });
                }}
                css={css`
                  padding: ${spacing.deci} ${spacing.regular};
                  font-size: 1.15rem;
                  font-weight: 500;
                  border-radius: 8px;
                  min-height: 56px;
                `}
              >
                {t('subscription.continue')}
              </Button>
            ) : isOnboardingSource && onboardingStep === 2 ? (
              <>
                {/* Screen 2: CTA + pricing text + links (no toggle — moved to body) */}
                <Button
                  variant="primary"
                  block
                  onClick={handleStartTrialClick}
                  disabled={isLoading || isFetchingOfferings || !selectedPackage || isCheckingEligibility}
                  css={css`
                    padding: ${spacing.deci} ${spacing.regular};
                    font-size: 1.15rem;
                    font-weight: 500;
                    border-radius: 8px;
                    min-height: 56px;
                  `}
                >
                  {isLoading
                    ? t('subscription.loading')
                    : isFetchingOfferings
                      ? t('subscription.loadingSubscriptionData')
                      : !selectedPackage
                        ? t('subscription.loadingPackages')
                        : isCheckingEligibility
                          ? t('subscription.justAMoment')
                          : t('subscription.startForFree')}
                </Button>

                <p
                  css={css`
                    ${fontStyles.cat}
                    color: ${colors.tungsten};
                    text-align: center;
                    margin: 0;

                    html[data-theme='dark'] & {
                      color: ${colors.cloud};
                    }
                  `}
                >
                  {isFetchingOfferings
                    ? t('subscription.connectingToPayment')
                    : isCheckingEligibility
                      ? t('subscription.perYear', {
                          price: `${currencySymbol}${shouldShowDiscount ? discountAnnualPrice : displayYearlyPrice}`,
                        })
                      : isTrialEligible || isTrialEligibilityUnknown
                        ? t('subscription.freeTrialPricing', {
                            price: `${currencySymbol}${shouldShowDiscount ? discountAnnualPrice : displayYearlyPrice}`,
                            days: offerTrialDays,
                          })
                        : t('subscription.yearlyPriceCancel', {
                            price: `${currencySymbol}${shouldShowDiscount ? discountAnnualPrice : displayYearlyPrice}`,
                          })}
                </p>

                <div
                  css={css`
                    ${fontStyles.cat}
                    text-align: center;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: ${spacing.milli};
                  `}
                >
                  {selectedPackage && (
                    <>
                      <button
                        onClick={() => {
                          try {
                            posthog.capture('View All Plans Opened', {
                              current_plan: subscriptionType,
                              weekly_price: displayWeeklyPrice,
                              yearly_price: displayYearlyPrice,
                              currency_symbol: currencySymbol,
                              savings_percentage: calculateSavingsPercentage(displayWeeklyPrice, displayYearlyPrice),
                            });
                          } catch (error) {
                            console.error('Failed to track view all plans event:', error);
                          }
                          setIsPlanSheetOpen(true);
                        }}
                        css={css`
                          color: ${colors.petrolBlue};
                          background: none;
                          border: none;
                          padding: ${spacing.centi} ${spacing.centi};
                          margin: 0;
                          cursor: pointer;
                          min-height: 44px;
                          display: inline-flex;
                          align-items: center;
                          white-space: nowrap;
                          -webkit-tap-highlight-color: transparent;
                          touch-action: manipulation;

                          html[data-theme='dark'] & {
                            color: ${colors.skyBlue};
                          }
                        `}
                      >
                        {t('subscription.seePlans')}
                      </button>
                      <span
                        css={css`
                          color: ${colors.tungsten};
                          html[data-theme='dark'] & {
                            color: ${colors.cloud};
                          }
                        `}
                      >
                        ·
                      </span>
                    </>
                  )}
                  <button
                    onClick={handleContinueFree}
                    css={css`
                      color: ${colors.petrolBlue};
                      background: none;
                      border: none;
                      padding: ${spacing.centi} ${spacing.centi};
                      margin: 0;
                      cursor: pointer;
                      min-height: 44px;
                      display: inline-flex;
                      align-items: center;
                      white-space: nowrap;
                      -webkit-tap-highlight-color: transparent;
                      touch-action: manipulation;

                      html[data-theme='dark'] & {
                        color: ${colors.skyBlue};
                      }
                    `}
                  >
                    {t('subscription.exploreFree')}
                  </button>
                  <span
                    css={css`
                      color: ${colors.tungsten};
                      html[data-theme='dark'] & {
                        color: ${colors.cloud};
                      }
                    `}
                  >
                    ·
                  </span>
                  <button
                    onClick={handleRestore}
                    disabled={isLoading}
                    css={css`
                      color: ${colors.petrolBlue};
                      background: none;
                      border: none;
                      padding: ${spacing.centi} ${spacing.centi};
                      margin: 0;
                      cursor: pointer;
                      min-height: 44px;
                      display: inline-flex;
                      align-items: center;
                      white-space: nowrap;
                      -webkit-tap-highlight-color: transparent;
                      touch-action: manipulation;

                      html[data-theme='dark'] & {
                        color: ${colors.skyBlue};
                      }
                    `}
                  >
                    {t('subscription.restore')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Non-onboarding: existing footer content unchanged */}
                {/* Toggle section - Only show when yearly is selected */}
                {subscriptionType === 'yearly' && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      padding: ${spacing.deci} 0;
                      background-color: ${colors.fog};
                      border-radius: 8px;
                      padding: ${spacing.deci};

                      html[data-theme='dark'] & {
                        background-color: ${colors.tungsten};
                      }
                    `}
                  >
                    <span
                      css={css`
                        ${fontStyles.lion}
                        color: ${colors.liquorice};

                        html[data-theme='dark'] & {
                          color: ${colors.sugar};
                        }
                      `}
                    >
                      {t('subscription.notifyBeforeTrialEnds')}
                    </span>
                    <ToggleSwitch checked={notifyBeforeTrial} onChange={handleNotifyToggle} />
                  </div>
                )}

                {/* Embedded Plan Selector - Only when NOT in onboarding and weekly package exists */}
                {!isOnboarding && hasWeeklyPackage && (
                  <div
                    css={css`
                      display: flex;
                      gap: 12px;
                    `}
                  >
                    {/* Weekly plan card */}
                    <div
                      css={css`
                        display: flex;
                        flex: 1;
                        border-radius: 12px;
                        border: 2px solid ${subscriptionType === 'weekly' ? colors.petrolBlue : colors.cloud};
                        background: ${subscriptionType === 'weekly' ? colors.leafAir : colors.sugar};
                        padding: ${spacing.deci};
                        cursor: pointer;
                        transition: all 0.2s ease;
                        justify-content: space-between;
                        align-items: center;

                        html[data-theme='dark'] & {
                          background: ${subscriptionType === 'weekly' ? colors.tungsten : colors.liquorice};
                          border-color: ${subscriptionType === 'weekly' ? colors.petrolBlue : colors.tungsten};
                        }
                      `}
                      onClick={() => setSubscriptionType('weekly')}
                    >
                      <div>
                        <div
                          css={css`
                            ${fontStyles.dog};
                            font-weight: 600;
                            color: ${colors.liquorice};
                            margin-bottom: 2px;

                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                          `}
                        >
                          {t('subscription.weekly')}
                        </div>
                        <div
                          css={css`
                            ${fontStyles.cat};
                            color: ${colors.liquorice};

                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                          `}
                        >
                          {currencySymbol}
                          {displayWeeklyPrice} {t('subscription.perWeekSuffix')}
                        </div>
                      </div>
                      <div
                        css={css`
                          width: 20px;
                          height: 20px;
                          border: 2px solid ${subscriptionType === 'weekly' ? colors.petrolBlue : colors.cloud};
                          border-radius: 6px;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          background-color: ${subscriptionType === 'weekly' ? colors.petrolBlue : 'transparent'};

                          html[data-theme='dark'] & {
                            border-color: ${subscriptionType === 'weekly' ? colors.petrolBlue : colors.tungsten};
                          }
                        `}
                      >
                        {subscriptionType === 'weekly' && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Yearly plan card */}
                    <div
                      css={css`
                        display: flex;
                        flex: 1;
                        border-radius: 12px;
                        border: 2px solid ${subscriptionType === 'yearly' ? colors.petrolBlue : colors.cloud};
                        background: ${subscriptionType === 'yearly' ? colors.leafAir : colors.sugar};
                        padding: ${spacing.deci};
                        cursor: pointer;
                        transition: all 0.2s ease;
                        justify-content: space-between;
                        align-items: center;
                        position: relative;

                        html[data-theme='dark'] & {
                          background: ${subscriptionType === 'yearly' ? colors.tungsten : colors.liquorice};
                          border-color: ${subscriptionType === 'yearly' ? colors.petrolBlue : colors.tungsten};
                        }
                      `}
                      onClick={() => setSubscriptionType('yearly')}
                    >
                      {/* 7 MO FREE badge */}
                      <div
                        css={css`
                          position: absolute;
                          top: -8px;
                          left: 0;
                          right: 0;
                          display: flex;
                          justify-content: center;
                          z-index: 10;
                        `}
                      >
                        <div
                          css={css`
                            background: ${colors.petrolBlue};
                            color: white;
                            ${fontStyles.mouse};
                            padding: 3px 10px 4px;
                            border-radius: 8px;
                            text-transform: uppercase;
                            font-size: 10px;
                            font-weight: 700;
                            letter-spacing: 0.5px;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
                          `}
                        >
                          {t('subscription.sevenMonthsFree')}
                        </div>
                      </div>

                      <div>
                        <div
                          css={css`
                            ${fontStyles.dog};
                            font-weight: 600;
                            color: ${colors.liquorice};
                            margin-bottom: 2px;

                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                          `}
                        >
                          {t('subscription.yearly')}
                        </div>
                        <div
                          css={css`
                            ${fontStyles.cat};
                            color: ${colors.liquorice};

                            html[data-theme='dark'] & {
                              color: ${colors.sugar};
                            }
                          `}
                        >
                          {currencySymbol}
                          {Math.round((displayYearlyPrice / 52) * 100) / 100} {t('subscription.perWeekSuffix')}
                        </div>
                      </div>
                      <div
                        css={css`
                          width: 20px;
                          height: 20px;
                          border: 2px solid ${subscriptionType === 'yearly' ? colors.petrolBlue : colors.cloud};
                          border-radius: 6px;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          background-color: ${subscriptionType === 'yearly' ? colors.petrolBlue : 'transparent'};

                          html[data-theme='dark'] & {
                            border-color: ${subscriptionType === 'yearly' ? colors.petrolBlue : colors.tungsten};
                          }
                        `}
                      >
                        {subscriptionType === 'yearly' && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Primary button */}
                <Button
                  variant="primary"
                  block
                  onClick={handleStartTrialClick}
                  disabled={isLoading || isFetchingOfferings || !selectedPackage || isCheckingEligibility}
                  css={css`
                    padding: ${spacing.deci} ${spacing.regular};
                    font-size: 1.15rem;
                    font-weight: 500;
                    border-radius: 8px;
                    min-height: 56px;
                  `}
                >
                  {isLoading
                    ? t('subscription.loading')
                    : isFetchingOfferings
                      ? t('subscription.loadingSubscriptionData')
                      : !selectedPackage
                        ? t('subscription.loadingPackages')
                        : isCheckingEligibility
                          ? t('subscription.justAMoment')
                          : subscriptionType === 'weekly'
                            ? t('subscription.subscribeToBloom')
                            : isTrialEligible
                              ? t('subscription.startForFree')
                              : isTrialEligibilityUnknown
                                ? t('subscription.startForFree')
                                : t('subscription.subscribeToBloom')}
                </Button>

                {/* Refresh button when packages are unavailable */}
                {!selectedPackage && !isFetchingOfferings && isNative && (
                  <Button
                    variant="secondary"
                    block
                    onClick={() => {
                      toast.info(t('subscription.refreshingPackages'));
                      fetchOfferings();
                    }}
                    disabled={isFetchingOfferings}
                    css={css`
                      padding: ${spacing.deci} ${spacing.regular};
                      font-size: 1rem;
                      font-weight: 500;
                      border-radius: 8px;
                      margin-top: ${spacing.deci};
                    `}
                  >
                    {t('subscription.refreshPackages')}
                  </Button>
                )}

                {/* Footer text */}
                <p
                  css={css`
                    ${fontStyles.cat}
                    color: ${colors.tungsten};
                    text-align: center;
                    margin: 0;

                    html[data-theme='dark'] & {
                      color: ${colors.cloud};
                    }
                  `}
                >
                  {isFetchingOfferings ? (
                    t('subscription.connectingToPayment')
                  ) : isCheckingEligibility ? (
                    subscriptionType === 'weekly' ? (
                      t('subscription.perWeek', { price: `${currencySymbol}${displayWeeklyPrice.toFixed(2)}` })
                    ) : (
                      t('subscription.perYear', { price: `${currencySymbol}${displayYearlyPrice}` })
                    )
                  ) : subscriptionType === 'weekly' ? (
                    t('subscription.weeklyPriceCancel', { price: `${currencySymbol}${displayWeeklyPrice.toFixed(2)}` })
                  ) : shouldShowDiscount ? (
                    isTrialEligible || isTrialEligibilityUnknown ? (
                      <Trans
                        i18nKey="subscription.freeTodayDiscounted"
                        values={{
                          originalPrice: `${currencySymbol}${displayYearlyPrice}`,
                          discountPrice: `${currencySymbol}${discountAnnualPrice}`,
                          days: offerTrialDays,
                        }}
                        components={{
                          s: (
                            <span
                              css={css`
                                text-decoration: line-through;
                                opacity: 0.6;
                              `}
                            />
                          ),
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey="subscription.discountedYearlyCancel"
                        values={{
                          originalPrice: `${currencySymbol}${displayYearlyPrice}`,
                          discountPrice: `${currencySymbol}${discountAnnualPrice}`,
                        }}
                        components={{
                          s: (
                            <span
                              css={css`
                                text-decoration: line-through;
                                opacity: 0.6;
                                margin-right: 4px;
                              `}
                            />
                          ),
                        }}
                      />
                    )
                  ) : isTrialEligible || isTrialEligibilityUnknown ? (
                    t('subscription.freeTrialPricing', {
                      price: `${currencySymbol}${displayYearlyPrice}`,
                      days: offerTrialDays,
                    })
                  ) : (
                    t('subscription.yearlyPriceCancel', { price: `${currencySymbol}${displayYearlyPrice}` })
                  )}
                </p>

                {/* See plans and continue free links */}
                <div
                  css={css`
                    ${fontStyles.cat}
                    text-align: center;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: ${spacing.centi};
                  `}
                >
                  {isOnboarding && selectedPackage && (
                    <>
                      <button
                        onClick={() => {
                          try {
                            posthog.capture('View All Plans Opened', {
                              current_plan: subscriptionType,
                              weekly_price: displayWeeklyPrice,
                              yearly_price: displayYearlyPrice,
                              currency_symbol: currencySymbol,
                              savings_percentage: calculateSavingsPercentage(displayWeeklyPrice, displayYearlyPrice),
                            });
                          } catch (error) {
                            console.error('Failed to track view all plans event:', error);
                          }
                          setIsPlanSheetOpen(true);
                        }}
                        css={css`
                          color: ${colors.petrolBlue};
                          background: none;
                          border: none;
                          padding: 0;
                          margin: 0;
                          cursor: pointer;

                          html[data-theme='dark'] & {
                            color: ${colors.skyBlue};
                          }
                        `}
                      >
                        {t('subscription.seePlans')}
                      </button>
                      <span
                        css={css`
                          color: ${colors.tungsten};
                          html[data-theme='dark'] & {
                            color: ${colors.cloud};
                          }
                        `}
                      >
                        ·
                      </span>
                    </>
                  )}
                  <button
                    onClick={handleContinueFree}
                    css={css`
                      color: ${colors.petrolBlue};
                      background: none;
                      border: none;
                      padding: 0;
                      margin: 0;
                      cursor: pointer;

                      html[data-theme='dark'] & {
                        color: ${colors.skyBlue};
                      }
                    `}
                  >
                    {t('subscription.exploreFree')}
                  </button>
                  <span
                    css={css`
                      color: ${colors.tungsten};
                      html[data-theme='dark'] & {
                        color: ${colors.cloud};
                      }
                    `}
                  >
                    ·
                  </span>
                  <button
                    onClick={handleRestore}
                    disabled={isLoading}
                    css={css`
                      color: ${colors.petrolBlue};
                      background: none;
                      border: none;
                      padding: 0;
                      margin: 0;
                      cursor: pointer;

                      html[data-theme='dark'] & {
                        color: ${colors.skyBlue};
                      }
                    `}
                  >
                    {t('subscription.restore')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* One-Time Offer Screen (Screen 3) — replaces WeeklyOfferModal */}
      {isOnboardingSource && onboardingStep === 3 && (
        <OneTimeOfferScreen
          onClose={handleClose}
          onRedeemOffer={() => {
            posthog.capture('Onboarding One-Time Offer Redeemed');
            handleStartTrial(true, 50);
          }}
          onExploreFree={() => {
            localStorage.setItem('oneTimeDiscountOffered', 'true');
            posthog.capture('Onboarding One-Time Offer Dismissed');
            handleContinueFree();
          }}
          originalAnnualPrice={displayYearlyPrice}
          discountAnnualPrice={discountAnnualPrice}
          currencySymbol={currencySymbol}
          isLoading={isLoading}
          isSkipUser={isSkipSource}
          freeTrialDays={offerTrialDays}
        />
      )}

      <PlanSelectionSheet
        isOpen={isPlanSheetOpen}
        onClose={() => {
          setIsPlanSheetOpen(false);
          // Reset to yearly when closing plan sheet on Screen 2, since Screen 2 only shows yearly pricing
          if (isOnboardingSource && onboardingStep === 2) {
            setSubscriptionType('yearly');
          }
        }}
        selectedType={subscriptionType}
        onPlanSelect={handlePlanSelect}
        weeklyPrice={displayWeeklyPrice}
        yearlyPrice={shouldShowDiscount ? discountAnnualPrice : displayYearlyPrice}
        currencySymbol={currencySymbol}
        freeTrialDays={offerTrialDays}
        onStartTrial={handleStartTrialClick}
        shouldShowDiscount={shouldShowDiscount}
        originalYearlyPrice={displayYearlyPrice}
        disabled={isLoading || isFetchingOfferings || !selectedPackage || isCheckingEligibility}
        buttonLabel={
          isLoading
            ? t('subscription.loading')
            : isFetchingOfferings
              ? t('subscription.loadingSubscriptionData')
              : !selectedPackage
                ? t('subscription.loadingPackages')
                : isCheckingEligibility
                  ? t('subscription.justAMoment')
                  : undefined
        }
      />

      {/* Notification Blocked Modal - shows when user needs to enable notifications in settings */}
      <NotificationBlockedModal
        isOpen={isNotificationBlockedModalOpen}
        onClose={() => setIsNotificationBlockedModalOpen(false)}
      />
    </div>
  );
};
